import { Preferences } from "@capacitor/preferences";
import { isNativePlatform, STORAGE_KEYS } from "./storage";

/**
 * Get access token from storage
 */
export const getAccessToken = async (): Promise<string | null> => {
	try {
		if (isNativePlatform()) {
			const { value } = await Preferences.get({
				key: STORAGE_KEYS.ACCESS_TOKEN,
			});
			return value;
		} else {
			return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
		}
	} catch (error) {
		console.error("Error getting access token:", error);
		return null;
	}
};

/**
 * Set access token in storage
 */
export const setAccessToken = async (token: string): Promise<void> => {
	try {
		if (isNativePlatform()) {
			await Preferences.set({
				key: STORAGE_KEYS.ACCESS_TOKEN,
				value: token,
			});
		} else {
			localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
		}
	} catch (error) {
		console.error("Error setting access token:", error);
	}
};

/**
 * Remove access token from storage
 */
export const removeAccessToken = async (): Promise<void> => {
	try {
		if (isNativePlatform()) {
			await Preferences.remove({ key: STORAGE_KEYS.ACCESS_TOKEN });
		} else {
			localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
		}
	} catch (error) {
		console.error("Error removing access token:", error);
	}
};

/**
 * Get email from storage
 */
export const getEmail = async (): Promise<string | null> => {
	try {
		if (isNativePlatform()) {
			const { value } = await Preferences.get({ key: STORAGE_KEYS.EMAIL });
			return value;
		} else {
			return localStorage.getItem(STORAGE_KEYS.EMAIL);
		}
	} catch (error) {
		console.error("Error getting email:", error);
		return null;
	}
};

/**
 * Set email in storage
 */
export const setEmail = async (email: string): Promise<void> => {
	try {
		if (isNativePlatform()) {
			await Preferences.set({
				key: STORAGE_KEYS.EMAIL,
				value: email,
			});
		} else {
			localStorage.setItem(STORAGE_KEYS.EMAIL, email);
		}
	} catch (error) {
		console.error("Error setting email:", error);
	}
};

/**
 * Remove email from storage
 */
export const removeEmail = async (): Promise<void> => {
	try {
		if (isNativePlatform()) {
			await Preferences.remove({ key: STORAGE_KEYS.EMAIL });
		} else {
			localStorage.removeItem(STORAGE_KEYS.EMAIL);
		}
	} catch (error) {
		console.error("Error removing email:", error);
	}
};

/**
 * Clear all auth data
 */
export const clearAuthData = async (): Promise<void> => {
	await Promise.all([removeAccessToken(), removeEmail()]);
};

/**
 * Synchronous fallback for legacy compatibility
 * Use these only when async is not possible (like router context initialization)
 */
export const getAccessTokenSync = (): string | null => {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

export const getEmailSync = (): string | null => {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(STORAGE_KEYS.EMAIL);
};
